
import { Component, ChangeDetectionStrategy, signal, inject, OnInit, computed } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PlatformService } from '../../../services/platform.service';
import { NotificationService } from '../../../services/notification.service';
import { Plan } from '../../../models/index';

@Component({
  selector: 'app-platform-plans',
  standalone: true,
  imports: [CommonModule, FormsModule, DecimalPipe],
  templateUrl: './platform-plans.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlatformPlansComponent implements OnInit {
  private platformService = inject(PlatformService);
  private notificationService = inject(NotificationService);

  plans = signal<Plan[]>([]);
  isLoading = signal(true);
  
  // Modal State
  isPlanModalOpen = signal(false);
  editingPlan = signal<Plan | null>(null);
  isProcessingAction = signal(false);

  // Features List for Checkboxes (Matched with Dashboard)
  featureList = [
      { key: 'core_pos', label: 'ระบบจุดขาย (Core POS)' },
      { key: 'telemedicine', label: 'แพทย์ทางไกล (Telemedicine)' },
      { key: 'reporting', label: 'รายงานผล (Reporting)' },
      { key: 'ai_order', label: 'สั่งงานด้วย AI (AI Order)' },
      { key: 'loyalty', label: 'ระบบสมาชิก (Loyalty)' },
      { key: 'partner_network', label: 'เครือข่ายพันธมิตร (Partner Network)' },
      { key: 'accounting', label: 'บัญชี (Accounting)' },
      { key: 'compliance_reports', label: 'รายงาน ภท. (Compliance)' },
      { key: 'multi_store', label: 'จัดการหลายสาขา (Multi-Store)' },
      { key: 'kiosk', label: 'ตู้ลงทะเบียนคนไข้ (Patient Kiosk)' },
      { key: 'nfc_cards', label: 'ระบบบัตรสมาชิก NFC' },
      { key: 'automated_promptpay', label: 'QR PromptPay อัตโนมัติ' }
  ];

  clinicPlans = computed(() => {
      return this.plans().filter(p => !p.id.toLowerCase().startsWith('tier_d'));
  });

  dispensaryPlans = computed(() => {
      return this.plans().filter(p => p.id.toLowerCase().startsWith('tier_d'));
  });

  ngOnInit() {
    this.loadPlans();
  }

  async loadPlans() {
    this.isLoading.set(true);
    try {
      this.plans.set(await this.platformService.getPlans());
    } catch (e: any) {
      this.notificationService.showError("Failed to load plans: " + e.message);
    } finally {
      this.isLoading.set(false);
    }
  }

  editPlan(plan: Plan) {
      // Deep copy to avoid mutating the signal directly before saving
      this.editingPlan.set(JSON.parse(JSON.stringify(plan))); 
      this.isPlanModalOpen.set(true);
  }

  closePlanModal() {
      this.isPlanModalOpen.set(false);
      this.editingPlan.set(null);
  }

  togglePlanFeature(key: string, event: Event) {
      const plan = this.editingPlan();
      if (plan) { 
          plan.features[key] = (event.target as HTMLInputElement).checked; 
          this.editingPlan.set({ ...plan }); 
      }
  }

  updatePlanLimit(key: string, value: string) {
      const plan = this.editingPlan();
      const numValue = value === '' ? null : Number(value);
      if (plan) { 
          (plan.limits as any)[key] = numValue; 
          this.editingPlan.set({ ...plan }); 
      }
  }

  async savePlan() {
      const plan = this.editingPlan(); 
      if (!plan) return;
      
      this.isProcessingAction.set(true);
      try { 
          await this.platformService.updatePlan(plan); 
          this.notificationService.showSuccess("Plan updated successfully"); 
          this.closePlanModal(); 
          this.loadPlans(); 
      } 
      catch (e: any) { 
          this.notificationService.showError("Failed to update plan: " + e.message); 
      } 
      finally { 
          this.isProcessingAction.set(false); 
      }
  }

  getPlanClass(planName: string | undefined): string {
      const name = (planName || '').toLowerCase();
      if (name.includes('enterprise') || name.includes('tier l')) return 'bg-purple-900/30 text-purple-300 border-purple-500/30';
      if (name.includes('growth') || name.includes('tier m')) return 'bg-blue-900/30 text-blue-300 border-blue-500/30';
      return 'bg-slate-800 text-slate-400 border-slate-600';
  }
}
